/**
 * <handover-email-address> — the E-Mail exit's own question, and the whole of
 * it: where the deployment sends this handover's link. It opens as its own
 * overlay above the handover sheet rather than unfolding inside it, so the
 * one thing being asked stands alone instead of competing with the sheet's
 * other three exits for the buyer's attention.
 *
 * The sheet underneath is not torn down while this stands — overlays stack —
 * so a buyer who dismisses this without sending comes back to the name they
 * typed and the tickets they picked, with the record-only exit still there to
 * keep the handover for a mail they send later.
 *
 * The address goes out on `handTicketsOver` and stops there: the deployment
 * sends the mail and writes the address to no document, so a resend asks for
 * it again. Here it lives on this element's own field and dies with the
 * element — no state cell, nothing persisted, nothing logged.
 *
 * The refusal stays in the field. A wrong address is a mail nobody ever sees,
 * and a toast after the send says so far too late to fix, so the send is held
 * until the address is one `handTicketsOver` would accept — measured against
 * that callable's own field schema, so what this field refuses and what the
 * call refuses cannot come apart.
 */
import type { SchmancyInput } from "@mhmo91/schmancy/form";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { HandTicketsOver } from "@momo/types";
import { html, type PropertyValues } from "lit";
import { property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { LocalizeMixin } from "../../i18n/localize.ts";

@schmancyElement("handover-email-address")
export class HandoverEmailAddress extends LocalizeMixin(SchmancyElement) {
	/** Who the sheet above named — this overlay only asks where they are
	 *  reached, so it says whose address it wants rather than making the buyer
	 *  hold that context themselves. */
	@property({ attribute: false }) recipient = "";

	@state() private address = "";

	@state() private busy = false;
	@state() private failed: "write" | "mail" | null = null;

	private readonly field = createRef<SchmancyInput & HTMLElement>();

	/** The refusal this address earns, or "" when it is one the callable would
	 *  accept. The contract's own field schema is the single authority here, so
	 *  an address this field lets through is never one the call then refuses —
	 *  which is the whole reason the rule is not restated as a regex. */
	private refusal(): string {
		return HandTicketsOver.HandTicketsOverInput.shape.recipientEmail.safeParse(
			this.address.trim(),
		).success
			? ""
			: this.t("storefront.orderAccess.handover.emailAddress.invalid");
	}

	protected override updated(changed: PropertyValues): void {
		super.updated(changed);
		// Live correction, and only that: a field already refusing re-asks on
		// every keystroke and clears the moment the address parses. A field not
		// yet refusing is left alone, because setCustomValidity paints on the
		// spot — it is platform truth, not a gated hint — so asserting it while
		// the buyer is still typing would redden a half-written address.
		if (this.field.value?.error)
			this.field.value.setCustomValidity(this.refusal());
	}

	private send(): void {
		if (this.busy) return;
		const refusal = this.refusal();
		if (refusal !== "") {
			this.field.value?.setCustomValidity(refusal);
			this.field.value?.focus();
			return;
		}
		this.busy = true;
		this.failed = null;
		this.dispatchEvent(
			new CustomEvent("send", {
				detail: {
					address: this.address.trim(),
					settle: (accepted: boolean, recorded: boolean) => {
						this.busy = false;
						if (accepted)
							this.dispatchEvent(
								new CustomEvent("close", { bubbles: true, composed: true }),
							);
						else this.failed = recorded ? "mail" : "write";
					},
				},
				bubbles: true,
				composed: true,
			}),
		);
	}

	override render() {
		return html`
			<!-- The measure the sheet's own root documents: the viewport clamp is
			     against 100vw rather than max-w-full, because the overlay's grid
			     mount is content-sized and a percentage there resolves against
			     this element's own measured width, clamping nothing. -->
			<div class="flex w-sm max-w-[calc(100vw-1.5rem)] flex-col gap-5 p-5">
				<div class="flex flex-col gap-1">
					<schmancy-typography type="headline" token="sm">
						${this.t("storefront.orderAccess.handover.emailAddress.title")}
					</schmancy-typography>
					<schmancy-typography type="body" token="md" class="text-surface-onVariant">
						${this.t("storefront.orderAccess.handover.emailAddress.lead", {
							name: this.recipient,
						})}
					</schmancy-typography>
				</div>

				<schmancy-input
					${ref(this.field)}
					inputmode="email"
					autocomplete="email"
					autofocus
					label=${this.t("storefront.orderAccess.handover.emailAddress.label")}
					placeholder=${this.t("storefront.orderAccess.handover.emailAddress.placeholder")}
					hint=${this.t("storefront.orderAccess.handover.emailAddress.hint")}
					?disabled=${this.busy}
					.value=${this.address}
					@input=${(e: CustomEvent<{ value: string }>) => {
						if (this.busy || e.detail.value === this.address) return;
						this.address = e.detail.value;
					}}
					@enter=${() => this.send()}
				></schmancy-input>

				<schmancy-button variant="filled" width="full" .busy=${this.busy} color=${this.failed ? "error" : "primary"} @click=${() => this.send()}>
					<schmancy-icon slot="prefix">send</schmancy-icon>
					${this.t(this.failed === "mail" ? "storefront.orderAccess.handover.emailAddress.retryMail" : this.failed === "write" ? "storefront.orderAccess.handover.sheet.retry" : "storefront.orderAccess.handover.emailAddress.send")}
				</schmancy-button>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"handover-email-address": HandoverEmailAddress;
	}
}
